// src/api/portalRefusalCopy.js
//
// WHAT WE TELL A SUBMITTER WHEN SOMETHING IS REFUSED.
//
// ── THE HARDEST ONE IS `unattributed`, SO IT IS WRITTEN FIRST ───────────────
//
// It is the case where the app answered and named no reason: a 400 "Bad
// request", a 500 "Failed to submit the plan." Three ways to be quietly
// dishonest about it, and the copy has to avoid all three at once.
//
//   1. CLAIMING TO KNOW WHAT HAPPENED. We do not. "There was a problem with
//      the server" over-claims -- a 400 with no code is more likely OUR client
//      sending something the route did not expect. The only honest content is
//      the shape of our own ignorance, said plainly.
//
//   2. BLAMING THE SUBMITTER. Nothing here is attributable to them, and the
//      uncoded strings make it worse rather than better: "Bad request" reads,
//      to a person who just picked a file, as a statement about the file.
//
//   3. INVITING A RETRY THAT FAILS IDENTICALLY. This is the one with teeth.
//      Telling someone to try again with the same 60 MB file wastes six minutes
//      of upload to arrive back here, and does it every time they follow the
//      advice. Worse than saying nothing.
//
// So `retry` is THREE-VALUED, not a boolean. "unknown" is a real answer and the
// most common honest one; a boolean would force every unknown into a promise or
// a prohibition, and both would be made up.
//
//   "no"       we know the identical request fails identically
//   "unknown"  it might work; we cannot tell, and we say so
//   "yes"      we expect it to work, for a stated reason
//
// ── THE UNCODED SERVER STRING IS NOT SHOWN, AND THAT IS MEASURED ────────────
//
// `refused_unrecognized` renders the server's `error` verbatim (§3.17).
// `unattributed` MUST NOT, and the reason is a property of the tree rather than
// a preference. Every uncoded string the portal surface can send -- 13 of them
// -- is a developer's:
//
//   400 "Bad request"   404 "Not found"   500 "Failed to create plan review"
//   500 "Failed to submit the plan."      500 "Failed to render the export."
//
// Each restates the OPERATION, never the cause, and "Bad request" actively
// misleads. The coded ones are submitter sentences -- "The file exceeds the
// 50 MB limit.", "That file could not be read as a vector dataset." -- because
// a code means somebody designed the refusal and wrote for the person reading
// it. That correlation is the whole justification for the split, so
// portalRefusalCopy.test.js asserts it against strings taken from the server
// rather than leaving it as a claim in a comment.
//
// ── THERE IS NO REFERENCE NUMBER TO OFFER, AND WE DO NOT INVENT ONE ─────────
//
// The obvious action for `unattributed` is "quote this id to support". Measured
// against the live server: no correlation id comes back. nginx sets
// X-Correlation-ID on the request TO admin-api, not on the response to the
// browser, so the client has nothing. REM-380. Until that changes the copy
// offers the TIME, which is true and findable in a log, rather than an
// identifier that would look authoritative and match nothing.

/**
 * @typedef {{title: string, detail: string, retry: "no"|"unknown"|"yes", action?: string}} Copy
 */

/** The four dispositions that are not a specific code. */
const DISPOSITION_COPY = Object.freeze({
  unattributed: Object.freeze({
    title: "Something went wrong, and we can't tell you what",
    // Says exactly three things, all of them true: an error occurred, it gave
    // no reason, and that gap is ours. It does not name a cause, does not
    // mention the file, and does not promise anything about trying again.
    detail:
      "The system reported an error without saying what the problem was. " +
      "That missing explanation is a gap on our side, not something you did. " +
      "If you get in touch, the time above is enough for us to find it in the log.",
    retry: "unknown",
    action: "contact",
  }),
  unreachable: Object.freeze({
    title: "Your request didn't reach us",
    detail:
      "Something between your browser and our servers stopped the request, " +
      "so nothing we can read came back.",
    retry: "unknown",
    action: "contact",
  }),
  // unreachable WITH a 413 is the one case in that group we can actually
  // diagnose, because the status says what was wrong: the body was too large
  // for the gateway in front of us. Measured, not guessed -- REM-376 §3.16.
  // THIS IS THE `retry: "no"` THAT MATTERS MOST. The identical request fails
  // identically, and it fails after uploading the whole file first.
  unreachable_413: Object.freeze({
    title: "That file is too large to upload",
    detail:
      "The upload was stopped before it reached us because it is over the size " +
      "limit. Sending the same file again will fail the same way — it needs to " +
      "be under 50 MB. Splitting the plan into separate submissions, or saving " +
      "it as a zipped shapefile rather than GeoJSON, usually gets there.",
    retry: "no",
    action: "choose_file",
  }),
  refused_unrecognized: Object.freeze({
    title: "That was refused",
    // The server's own sentence is substituted for `detail` by copyFor(). This
    // is the fallback for the case where there isn't one.
    detail: "The system refused the request but we don't have a fuller explanation to show.",
    retry: "unknown",
    action: "contact",
  }),
});

/**
 * Copy for every code the portal can refuse with. THE KEYS OF THIS OBJECT ARE
 * THE RECOGNISED SET -- portalRefusals.test.js asserts it equals
 * PORTAL_REFUSAL_CODES exactly, so "we recognise this code" and "we have copy
 * for this code" cannot drift into being different lists. A recognised code
 * with no copy would fall through to a generic, which is the hole this whole
 * split exists to close.
 */
const CODE_COPY = Object.freeze({
  /* ── the file itself ─────────────────────────────────────────────────── */
  no_extension: { title: "We can't tell what kind of file that is",
    detail: "The file has no extension, so there's no way to know how to read it. Accepted: .geojson, .json, .kml, .kmz, .zip.",
    retry: "no", action: "choose_file" },
  unsupported_format: { title: "That file type isn't supported",
    detail: "Accepted formats are .geojson, .json, .kml, .kmz, and a zipped shapefile (.zip).",
    retry: "no", action: "choose_file" },
  empty_file: { title: "That file is empty",
    detail: "The file arrived with no contents. If it opens correctly on your machine, it may not have finished saving.",
    retry: "no", action: "choose_file" },
  file_too_large: { title: "That file is too large",
    detail: "Plans need to be under 50 MB. Splitting into separate submissions, or saving as a zipped shapefile rather than GeoJSON, usually gets there.",
    retry: "no", action: "choose_file" },
  upload_form_invalid: { title: "The upload didn't arrive the way we expect",
    // A client bug, not the submitter's. Says so without jargon, and does not
    // ask them to change the file -- changing it would not help.
    detail: "This is a fault in the page rather than anything about your file. Reloading the page and trying once more sometimes clears it.",
    retry: "unknown", action: "reload" },
  upload_incomplete: { title: "The upload didn't arrive complete",
    // retry:"unknown" deliberately. This covers a dropped connection, where
    // trying again works, AND a malformed request from a client bug, where it
    // will not. We cannot tell which from here, so we do not promise.
    detail: "Part of the file didn't make it. That is sometimes a dropped connection and sometimes a fault in the page, and we can't tell which from here.",
    retry: "unknown", action: "reload" },
  crs_undeclared: { title: "That file doesn't say what projection it uses",
    // §3.3: refused rather than defaulted, on purpose. The copy says why, because
    // "just assume WGS84" is what the submitter will otherwise expect us to do.
    detail: "Without a projection we can't place the plan on the map, and guessing could put it in the wrong location. For a shapefile, include the .prj file in the zip. For GeoJSON, save it as WGS 84 (EPSG:4326).",
    retry: "no", action: "choose_file" },
  unreadable_source: { title: "We couldn't read that file",
    detail: "The file has one of the right extensions but couldn't be opened as a vector dataset. It may be corrupt, or zipped in a way we can't unpack.",
    retry: "no", action: "choose_file" },
  no_layers: { title: "That file has no map layers in it",
    detail: "The file opened, but there was nothing drawable inside.",
    retry: "no", action: "choose_file" },
  too_many_layers: { title: "That file has too many layers",
    detail: "We can show up to 50 layers from one file. Export just the layer with the plan in it and submit that.",
    retry: "no", action: "choose_file" },
  unknown_sublayer: { title: "That layer is no longer available",
    detail: "The layer you picked isn't in the uploaded file any more. Start the upload again and choose from the list.",
    retry: "no", action: "restart_upload" },
  conversion_failed: { title: "We couldn't convert that layer",
    detail: "The layer was found but couldn't be turned into shapes we can store. Another layer in the same file may work.",
    retry: "no", action: "choose_file" },
  not_a_feature_collection: { title: "That layer isn't in a shape we can read",
    detail: "The converted layer didn't come back as a set of features.",
    retry: "unknown", action: "contact" },

  /* ── the staging token ───────────────────────────────────────────────── */
  token_expired: { title: "That upload has expired",
    // retry:"yes" and it means something specific: not this request, the FLOW.
    // The action is what disambiguates.
    detail: "Uploads are held for a short time while you review them. Upload the file again and you can carry on from there.",
    retry: "yes", action: "restart_upload" },
  token_scope_mismatch: { title: "That upload belongs to a different plan review",
    detail: "Upload the file again from this plan review.",
    retry: "yes", action: "restart_upload" },

  /* ── preparing the features ──────────────────────────────────────────── */
  no_features: { title: "That layer is empty",
    detail: "The layer exists but has nothing in it to submit.",
    retry: "no", action: "choose_file" },
  unsupported_geometry: { title: "That plan contains a shape we can't accept",
    // prepareFeatures refuses the WHOLE file on one bad geometry (§3.15), and a
    // submitter needs to know that before they go hunting for a partial fix.
    detail: "One or more shapes aren't a point, line or area — a GeometryCollection is the usual cause. The whole file is refused rather than part of it, so the shape needs fixing in your own software first.",
    retry: "no", action: "choose_file" },
  geom_invalid: { title: "That plan contains an invalid shape",
    detail: "A shape in the file is malformed — most often an area whose outline crosses itself. Most GIS tools have a repair or 'make valid' command that fixes this.",
    retry: "no", action: "choose_file" },

  /* ── tagging ─────────────────────────────────────────────────────────── */
  tags_missing: { title: "Nothing was tagged",
    detail: "Every shape needs a type before the plan can be submitted.",
    retry: "no" },
  tag_incomplete: { title: "Some shapes aren't tagged yet",
    detail: "Every shape needs a type before the plan can be submitted.",
    retry: "no" },
  tag_unknown_feature: { title: "A tag doesn't match the plan",
    // Reachable by a UI bug as well as a stale page, so the copy does not assert
    // which, and offers the action that fixes both.
    detail: "One of the tags refers to a shape that isn't in this upload. Reloading and tagging again will clear it.",
    retry: "unknown", action: "reload" },
  tag_ordinal_invalid: { title: "A tag doesn't match the plan",
    detail: "One of the tags refers to a shape that isn't in this upload. Reloading and tagging again will clear it.",
    retry: "unknown", action: "reload" },
  tag_value_invalid: { title: "One of the tags isn't a type we recognize",
    detail: "Choose a type from the list for each shape.",
    retry: "no" },
  tag_duplicate: { title: "A shape was tagged twice",
    detail: "Each shape takes one type. Reloading and tagging again will clear it.",
    retry: "unknown", action: "reload" },

  /* ── where the review is in its life ─────────────────────────────────── */
  not_awaiting_submission: { title: "This plan review isn't waiting for a submission",
    detail: "It may already have been submitted, or returned to you with findings. Reload to see where it stands.",
    retry: "no", action: "reload" },
  // THIS ONE DOES NOT BLAME THE PERSON AND DOES NOT INVITE A RETRY THAT WILL
  // FAIL IDENTICALLY. The org the request resolved into is gone — deleted
  // between the token being issued and the insert running. Nothing the
  // submitter did caused it and nothing they can do fixes it.
  // ── THE TWO GRANT REFUSALS SAY DIFFERENT THINGS ON PURPOSE ──────────────
  //
  // "never accepted" and "no longer accepts" are different facts about a
  // relationship, and only one of them is worth a phone call. Collapsing them
  // into one sentence would tell a submitter whose lease just ended the same
  // thing it tells someone who picked the wrong company from a list.
  //
  // Neither invites a retry: retrying is not what fixes either.
  no_submission_grant: { title: "That organization doesn't accept submissions from yours",
    detail: "They haven't granted your organization permission to submit plans to them. If you think that's wrong, they're the ones who can grant it.",
    retry: "no" },
  submission_grant_revoked: { title: "That organization no longer accepts submissions from yours",
    detail: "Permission to submit was withdrawn. Plans you already submitted to them are still here and still readable — you just can't send new ones.",
    retry: "no" },
  owner_org_id_invalid: { title: "That organization wasn't recognized",
    detail: "Choose the organization from the list rather than entering one.",
    retry: "no" },
  // REACHABLE ONLY IF THE FORM IS BYPASSED. The picker will not submit without
  // a choice, so a user meeting this sentence is using a client that does not
  // ask -- which is exactly the case that filed LP-2026-0001. The copy names
  // the missing thing rather than apologising for a rejection.
  owner_org_id_required: { title: "Choose who this plan review is for",
    detail: "Your organization can submit to more than one, so we won't guess. Pick the organization from the list — or your own, if this plan is yours.",
    retry: "no" },
  org_unavailable: { title: "Your organization is no longer available",
    detail: "This account's organization was removed while you were working. Nothing you entered was saved. Contact your administrator.",
    retry: "no" },
  not_awaiting_review: { title: "This plan review isn't waiting to be reviewed",
    detail: "Reload to see where it stands.", retry: "no", action: "reload" },
  not_open_for_markup: { title: "This plan review isn't open for findings",
    detail: "Findings can only be added while a review is with the reviewer. Reload to see where it stands.",
    retry: "no", action: "reload" },
  nothing_to_return: { title: "There are no findings to return",
    detail: "A plan review is returned with at least one finding on it. Add a finding first.",
    retry: "no" },
  review_approved: { title: "This plan review has been approved",
    detail: "An approved review is final and can't be changed.", retry: "no", action: "reload" },
  no_current_revision: { title: "Nothing has been submitted yet",
    detail: "There's no revision on this plan review to work with.", retry: "no", action: "reload" },

  /* ── findings ────────────────────────────────────────────────────────── */
  markup_type_unknown: { title: "That isn't a kind of finding we recognize",
    detail: "Choose one of the listed finding types.", retry: "no" },
  note_too_short: { title: "That finding needs a note",
    detail: "A finding carries an explanation so the submitter knows what to change.", retry: "no" },
  markup_not_found: { title: "That finding is no longer there",
    detail: "It may already have been retracted. Reload to see the current findings.",
    retry: "no", action: "reload" },
  not_the_markup_author: { title: "Only the person who wrote a finding can retract it",
    detail: "", retry: "no" },
  markup_frozen: { title: "That finding is frozen",
    detail: "Findings are fixed once a review is returned, so the submitter and the reviewer are looking at the same thing.",
    retry: "no", action: "reload" },
  unfrozen_markup: { title: "That finding isn't visible yet",
    detail: "Findings become visible to the submitter when the review is returned.",
    retry: "no", action: "reload" },
  feature_id_invalid: { title: "That finding isn't attached to a valid shape",
    detail: "Reload and place the finding again.", retry: "unknown", action: "reload" },
  feature_not_on_current_revision: { title: "That shape isn't in the current revision",
    detail: "A newer revision has been submitted. Reload to see it.", retry: "no", action: "reload" },

  /* ── the conversation ────────────────────────────────────────────────── */
  note_body_required: { title: "That message is empty",
    detail: "Write something before sending.", retry: "no" },
  note_body_too_long: { title: "That message is too long",
    detail: "Messages can be up to 4000 characters.", retry: "no" },
  parent_id_invalid: { title: "We couldn't find the message you're replying to",
    detail: "Reload the conversation and reply again.", retry: "unknown", action: "reload" },
  parent_is_a_reply: { title: "Replies only go one level deep",
    detail: "Reply to the original message instead.", retry: "no" },
  parent_not_in_thread: { title: "That message isn't in this conversation",
    detail: "Reload the conversation and reply again.", retry: "unknown", action: "reload" },
  bad_cursor: { title: "We lost your place in the conversation",
    detail: "Reload to start from the most recent messages.", retry: "no", action: "reload" },

  /* ── export ──────────────────────────────────────────────────────────── */
  unsupported_export_format: { title: "That download format isn't supported",
    detail: "Choose one of the offered formats.", retry: "no" },
  revision_not_exportable: { title: "That revision can't be downloaded",
    detail: "Only submitted revisions can be downloaded.", retry: "no", action: "reload" },
  export_not_audited: { title: "We couldn't record that download, so we didn't send it",
    // §3.14: the download is refused rather than served unrecorded, and that is
    // deliberate and against local precedent. The copy owns the decision rather
    // than implying a malfunction.
    detail: "Every download of a plan is recorded. The recording failed, and rather than hand over a copy we can't account for, we stopped. This one is worth telling us about.",
    retry: "unknown", action: "contact" },

  /* ── the gate ────────────────────────────────────────────────────────── */
  restricted_account: { title: "This account can't reach that",
    detail: "Your account is set up for the plan review portal only.", retry: "no" },
});

/** The codes we have copy for. Frozen array — see portalRefusals.js on Sets. */
export const COPY_CODES = Object.freeze(Object.keys(CODE_COPY).sort());

/**
 * The copy for a failure.
 *
 * @param {{status?: number, code?: string|null, payload?: object|null}} err
 * @param {string} disposition  from dispositionFor()
 * @returns {Copy}
 */
export function copyFor(err, disposition) {
  if (disposition === "refused") {
    // Object.hasOwn, NOT a truthy lookup. `CODE_COPY["constructor"]` is a
    // function and would sail past a falsy check, then throw on `.title` --
    // REM-367's idiom, which this codebase has shipped twice.
    const code = err?.code;
    if (typeof code === "string" && Object.hasOwn(CODE_COPY, code)) {
      return Object.freeze({ ...CODE_COPY[code] });
    }
    // dispositionFor said `refused`, so this is unreachable unless the two
    // lists have drifted. Answer as if unrecognised rather than throwing: the
    // submitter gets the server's sentence, which is the safe degradation.
    return unrecognizedCopy(err);
  }
  if (disposition === "refused_unrecognized") return unrecognizedCopy(err);
  if (disposition === "unreachable") {
    return err?.status === 413 ? DISPOSITION_COPY.unreachable_413 : DISPOSITION_COPY.unreachable;
  }
  return DISPOSITION_COPY.unattributed;
}

function unrecognizedCopy(err) {
  const stated = err?.payload?.error;
  const detail = typeof stated === "string" && stated.trim().length > 0 ? stated.trim() : null;
  return Object.freeze({
    ...DISPOSITION_COPY.refused_unrecognized,
    ...(detail ? { detail } : {}),
  });
}
